import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

const ONBOARDING_CACHE_PREFIX = "s1_onboarding_completed";

function getCacheKey(userId: string): string {
  return `${ONBOARDING_CACHE_PREFIX}_${userId}`;
}

export function useOnboardingStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["onboarding-status", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("onboarding_completed_at")
        .eq("id", user!.id)
        .single();

      if (error) throw error;

      if (data?.onboarding_completed_at) {
        localStorage.setItem(getCacheKey(user!.id), "true");
      }

      return data;
    },
    enabled: !!user,
  });

  const markCompleted = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("users")
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq("id", user!.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (user) localStorage.setItem(getCacheKey(user.id), "true");
      queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
    },
  });

  const localCache = user
    ? localStorage.getItem(getCacheKey(user.id)) === "true"
    : false;

  return {
    isCompleted: localCache || !!query.data?.onboarding_completed_at,
    isLoading: !localCache && query.isLoading,
    markCompleted: markCompleted.mutate,
  };
}
